import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { FaBars, FaBell, FaUserCircle, FaCog, FaSignOutAlt, FaCircle, FaEnvelopeOpenText } from 'react-icons/fa';


import { ENDPOINTS } from '../../utils/api-config';
import api from '../../utils/api.service';
import AdminProfileModal from './AdminProfileModal';
import './Navbar.css';

const Navbar = ({ toggleSidebar, sidebarOpen, logout }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const formatDistanceToNow = (date) => {
    try {
      const now = new Date();
      const diff = Math.floor((now - date) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return `${Math.floor(diff / 86400)}d ago`;
    } catch (e) {
      return 'Recently';
    }
  };



  const fetchProfile = async () => {
    try {
      const response = await api.get('/auth/profile');
      // Unwrap the TransformInterceptor format if present
      const profileData = response.data || response;
      setAdminData(profileData);
    } catch (err) {
      console.error('Failed to fetch admin profile', err);
    }
  };

  const fetchStats = async () => {
    try {
      const [statsRes, pendingRes] = await Promise.all([
        api.get(ENDPOINTS.DASHBOARD.STATS),
        api.get(ENDPOINTS.PROVIDERS.APPROVALS)
      ]);
      const dashboardStats = statsRes.data || statsRes;
      const pendingData = pendingRes.data || pendingRes;

      setStats({
        ...dashboardStats,
        pendingApprovals: Array.isArray(pendingData) ? pendingData.length : (pendingData?.data?.length || 0)
      });
    } catch (err) {
      console.error('Failed to fetch navbar stats', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get(ENDPOINTS.SUPPORT.ADMIN_NOTIFICATIONS);
      setNotifications(response.data || response || []);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchStats();
    fetchNotifications();

    // Refresh every 30 seconds for notifications (balanced for performance and responsiveness)
    const notificationInterval = setInterval(fetchNotifications, 30 * 1000);

    const statsInterval = setInterval(fetchStats, 5 * 60 * 1000);

    // Listen for manual refresh events (e.g., from TicketDetails)
    const handleRefresh = () => fetchNotifications();
    window.addEventListener('refreshNotifications', handleRefresh);

    return () => {
      clearInterval(notificationInterval);
      clearInterval(statsInterval);
      window.removeEventListener('refreshNotifications', handleRefresh);
    };
  }, []);

  // Re-fetch notifications when location changes to ensure badge clears when viewing a ticket
  useEffect(() => {
    fetchNotifications();
  }, [location.pathname]);


  const handleLogout = () => {
    if (logout) {
      logout();
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      window.location.reload();
    }
  };

  const handleOpenProfileModal = () => {
    setShowProfile(false);
    setIsProfileModalOpen(true);
  };

  const getProfileName = () => {
    if (!adminData) return 'Admin';
    if (adminData.firstName || adminData.lastName) {
      return `${adminData.firstName || ''} ${adminData.lastName || ''}`.trim();
    }
    return adminData.email ? adminData.email.split('@')[0] : 'Admin User';
  };

  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    if (pathnames.length === 0) return [{ label: 'Dashboard', path: '/dashboard' }];

    const breadcrumbMap = {
      'dashboard': 'Dashboard',
      'providers': 'Providers',
      'bookings': 'Bookings',
      'customers': 'Customers',
      'payments': 'Payments',
      'payouts': 'Payouts',
      'refunds': 'Refunds',
      'analytics': 'Analytics',
      'settings': 'Settings',
      'services': 'Services',
      'categories': 'Categories',
      'loyalty': 'Loyalty',
      'wallet': 'Wallet',
      'support': 'Support',
      'tickets': 'Tickets',
      'content': 'Content',
      'audit-log': 'Audit Log',
      'create': 'New',
      'edit': 'Edit',
      'approval': 'Approvals'
    };

    return pathnames.map((name, index) => {
      const path = `/${pathnames.slice(0, index + 1).join('/')}`;
      const isParam = /^[0-9a-fA-F-]{24,}|^\d+$/.test(name); // Detect IDs
      return {
        label: isParam ? 'Details' : (breadcrumbMap[name.toLowerCase()] || name),
        path
      };
    });
  };

  const getProfileInitials = () => {
    if (!adminData) return 'AD';
    if (adminData.firstName || adminData.lastName) {
      return `${(adminData.firstName || '')[0] || ''}${(adminData.lastName || '')[0] || ''}`.toUpperCase() || 'AD';
    }
    return adminData.email ? adminData.email[0].toUpperCase() : 'AD';
  };

  return (
    <>
      <nav className={`navbar ${sidebarOpen ? '' : 'sidebar-closed'}`}>
        <div className="navbar-left">
          <button className="menu-toggle" onClick={toggleSidebar}>
            <FaBars />
          </button>

          <div className="navbar-context">
            <div className="breadcrumbs">
              <Link to="/dashboard" className="breadcrumb-item home">Home</Link>
              {getBreadcrumbs().map((crumb, i) => (
                <React.Fragment key={crumb.path}>
                  <span className="breadcrumb-separator">/</span>
                  <Link to={crumb.path} className={`breadcrumb-item ${i === getBreadcrumbs().length - 1 ? 'active' : ''}`}>
                    {crumb.label}
                  </Link>
                </React.Fragment>
              ))}
            </div>

            {/* <div className="system-status">
              <span className="status-separator">•</span>
              <div className="status-text-container">
                <FaCircle className="status-dot pulsing" />
                <span className="status-sentence">
                  {!loading && stats ? (
                    <>
                      Platform is live <span className="status-highlight"></span> 
                      {stats.pendingApprovals > 0 ? (
                        <> with <span className="status-urgent">{stats.pendingApprovals} reviews pending</span></>
                      ) : (
                        ' & running smoothly'
                      )}
                    </>
                  ) : (
                    'Checking system status...'
                  )}
                </span>
              </div>
            </div> */}
          </div>
        </div>

        <div className="navbar-right">
          <div className="navbar-item notification-container">
            <button
              className={`icon-btn ${showNotifications ? 'active' : ''}`}
              onClick={() => {
                console.log('Toggling notifications, current state:', showNotifications);
                setShowNotifications(!showNotifications);
                setShowProfile(false);
              }}

            >
              <FaBell />
              {notifications.length > 0 && (
                <span className="notification-badge-count">{notifications.length}</span>
              )}
            </button>

            {showNotifications && (
              <div className="dropdown-menu notification-dropdown">
                <div className="dropdown-header">
                  <h3>Notifications</h3>
                  <span className="badge bg-primary">{notifications.length} New</span>
                </div>
                <div className="notifications-list">
                  {notifications.length > 0 ? (
                    notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className="notification-item"
                        onClick={() => {
                          setShowNotifications(false);
                          navigate(`/support/tickets/${notif.ticketId}`);
                        }}
                      >
                        <div className="notification-icon-wrapper">
                          <FaEnvelopeOpenText />
                        </div>
                        <div className="notification-content">
                          <h4>New Message from {notif.senderName}</h4>
                          <p className="notif-text">"{notif.message.substring(0, 50)}{notif.message.length > 50 ? '...' : ''}"</p>
                          <span className="notif-time">
                            {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                        <div className="notif-dot-unread"></div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-notifications">
                      <FaBell style={{ fontSize: '24px', opacity: 0.3, marginBottom: '10px' }} />
                      <p>No new messages</p>
                    </div>
                  )}
                </div>
                <div className="dropdown-footer">
                  <Link to="/support/tickets" onClick={() => setShowNotifications(false)}>View All Tickets</Link>
                </div>
              </div>
            )}
          </div>


          <div className="navbar-item profile-container">
            <button
              className="profile-btn"
              onClick={() => setIsProfileModalOpen(true)}
            >
              <div className="profile-avatar-small" style={{
                background: 'linear-gradient(135deg, var(--primary-color, #39b896), #2e967a)',
                color: 'white',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                {getProfileInitials()}
              </div>
              <div className="profile-info">
                <span className="profile-name">
                  {getProfileName()}
                </span>
                <span className="profile-role">
                  {adminData?.role ? adminData.role.charAt(0).toUpperCase() + adminData.role.slice(1) : 'Administrator'}
                </span>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {isProfileModalOpen && (
        <AdminProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          adminData={adminData}
          onUpdateSuccess={fetchProfile}
        />
      )}
    </>
  );
};

export default Navbar;